/**
 * Utilidades para métricas y monitoreo del sistema de emails
 */

import { METRICS_CONFIG } from '../config/emailConfig.js';

// Almacén en memoria para métricas (en producción se podría usar una base de datos)
const metricsStore = {
  emailsSent: 0,
  emailsFailed: 0,
  totalDuration: 0,
  retryAttempts: 0,
  lastReset: Date.now(),
  errors: [],
  successfulEmails: [],
  failedEmails: []
};

/**
 * Registra el envío exitoso de un email
 * @param {Object} emailData - Datos del email enviado
 */
export const recordEmailSuccess = (emailData) => {
  if (!METRICS_CONFIG.ENABLE_METRICS) return;
  
  metricsStore.emailsSent++;
  
  if (METRICS_CONFIG.TRACK_EMAIL_DURATION && emailData.duration) {
    metricsStore.totalDuration += emailData.duration;
  }
  
  if (METRICS_CONFIG.TRACK_RETRY_ATTEMPTS && emailData.attempt) {
    metricsStore.retryAttempts += emailData.attempt - 1; // Restar 1 porque el primer intento no es un retry
  }
  
  // Mantener solo los últimos 100 emails exitosos
  metricsStore.successfulEmails.push({
    timestamp: new Date().toISOString(),
    requestId: emailData.requestId,
    duration: emailData.duration,
    attempt: emailData.attempt,
    mode: emailData.mode
  });
  
  if (metricsStore.successfulEmails.length > 100) {
    metricsStore.successfulEmails.shift();
  }
};

/**
 * Registra el fallo en el envío de un email
 * @param {Object} errorData - Datos del error
 */
export const recordEmailFailure = (errorData) => {
  if (!METRICS_CONFIG.ENABLE_METRICS) return;
  
  metricsStore.emailsFailed++;
  
  // Mantener solo los últimos 50 errores
  metricsStore.errors.push({
    timestamp: new Date().toISOString(),
    requestId: errorData.requestId,
    errors: errorData.errors,
    message: errorData.message,
    duration: errorData.duration
  });
  
  metricsStore.failedEmails.push({
    timestamp: new Date().toISOString(),
    requestId: errorData.requestId,
    errors: errorData.errors,
    message: errorData.message
  });
  
  if (metricsStore.errors.length > 50) {
    metricsStore.errors.shift();
  }
  
  if (metricsStore.failedEmails.length > 50) {
    metricsStore.failedEmails.shift();
  }
};

/**
 * Obtiene las métricas actuales del sistema
 * @returns {Object} - Métricas del sistema
 */
export const getEmailMetrics = () => {
  const totalEmails = metricsStore.emailsSent + metricsStore.emailsFailed;
  const successRate = totalEmails > 0 ? (metricsStore.emailsSent / totalEmails) * 100 : 0;
  const averageDuration = metricsStore.emailsSent > 0 ? metricsStore.totalDuration / metricsStore.emailsSent : 0;
  const uptime = Date.now() - metricsStore.lastReset;
  
  return {
    summary: {
      totalEmailsSent: metricsStore.emailsSent,
      totalEmailsFailed: metricsStore.emailsFailed,
      totalEmails: totalEmails,
      successRate: Math.round(successRate * 100) / 100,
      averageDuration: Math.round(averageDuration),
      totalRetryAttempts: metricsStore.retryAttempts,
      uptime: uptime,
      uptimeFormatted: formatDuration(uptime)
    },
    recentActivity: {
      recentSuccesses: metricsStore.successfulEmails.slice(-10),
      recentFailures: metricsStore.failedEmails.slice(-10),
      recentErrors: metricsStore.errors.slice(-10)
    },
    health: {
      status: getHealthStatus(successRate, metricsStore.errors),
      lastError: metricsStore.errors.length > 0 ? metricsStore.errors[metricsStore.errors.length - 1] : null,
      errorRate: totalEmails > 0 ? (metricsStore.emailsFailed / totalEmails) * 100 : 0
    }
  };
};

/**
 * Determina el estado de salud del sistema
 * @param {number} successRate - Tasa de éxito
 * @param {Array} errors - Lista de errores recientes
 * @returns {string} - Estado de salud
 */
const getHealthStatus = (successRate, errors) => {
  const recentErrors = errors.filter(error => {
    const errorTime = new Date(error.timestamp).getTime();
    const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
    return errorTime > fiveMinutesAgo;
  });
  
  if (recentErrors.length > 5) {
    return 'critical';
  } else if (successRate < 80) {
    return 'warning';
  } else if (successRate < 95) {
    return 'degraded';
  } else {
    return 'healthy';
  }
};

/**
 * Formatea una duración en milisegundos a un formato legible
 * @param {number} ms - Duración en milisegundos
 * @returns {string} - Duración formateada
 */
const formatDuration = (ms) => {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) {
    return `${days}d ${hours % 24}h ${minutes % 60}m`;
  } else if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
};

/**
 * Reinicia las métricas del sistema
 */
export const resetMetrics = () => {
  metricsStore.emailsSent = 0;
  metricsStore.emailsFailed = 0;
  metricsStore.totalDuration = 0;
  metricsStore.retryAttempts = 0;
  metricsStore.lastReset = Date.now();
  metricsStore.errors = [];
  metricsStore.successfulEmails = [];
  metricsStore.failedEmails = [];
  
  console.log('📊 Métricas de email reiniciadas:', {
    timestamp: new Date().toISOString()
  });
};

/**
 * Genera un reporte de métricas para logging
 * @returns {Object} - Reporte de métricas
 */
export const generateMetricsReport = () => {
  const metrics = getEmailMetrics();
  
  return {
    timestamp: new Date().toISOString(),
    summary: metrics.summary,
    health: metrics.health,
    recommendations: generateRecommendations(metrics)
  };
};

/**
 * Genera recomendaciones basadas en las métricas
 * @param {Object} metrics - Métricas del sistema
 * @returns {Array} - Lista de recomendaciones
 */
const generateRecommendations = (metrics) => {
  const recommendations = [];
  
  if (metrics.health.status === 'critical') {
    recommendations.push('🚨 Sistema crítico: Revisar configuración de email inmediatamente');
  }
  
  if (metrics.summary.successRate < 90) {
    recommendations.push('⚠️ Tasa de éxito baja: Revisar configuración de red y credenciales');
  }
  
  if (metrics.summary.averageDuration > 10000) {
    recommendations.push('🐌 Respuesta lenta: Considerar optimizar timeout o servidor');
  }
  
  if (metrics.summary.totalRetryAttempts > metrics.summary.totalEmailsSent) {
    recommendations.push('🔄 Muchos reintentos: Revisar estabilidad de la conexión');
  }
  
  if (recommendations.length === 0) {
    recommendations.push('✅ Sistema funcionando correctamente');
  }
  
  return recommendations;
};

/**
 * Middleware para registrar métricas automáticamente
 * @param {Function} emailFunction - Función de email a envolver
 * @returns {Function} - Función envuelta con métricas
 */
export const withMetrics = (emailFunction) => {
  return async (...args) => {
    const startTime = Date.now();
    
    try {
      const result = await emailFunction(...args);
      
      if (result.success) {
        recordEmailSuccess({
          ...result,
          duration: Date.now() - startTime
        });
      } else {
        recordEmailFailure({
          ...result,
          duration: Date.now() - startTime
        });
      }
      
      return result;
    } catch (error) {
      recordEmailFailure({
        requestId: Math.random().toString(36).substr(2, 9),
        errors: [error.message],
        message: 'Error no controlado en función de email',
        duration: Date.now() - startTime
      });
      
      throw error;
    }
  };
};

// Exportar métricas por defecto
export default {
  recordEmailSuccess,
  recordEmailFailure,
  getEmailMetrics,
  resetMetrics,
  generateMetricsReport,
  withMetrics
};